import { getLocale, translate } from '../i18n'
import type { SyncFailureState } from './syncEngine'

type SyncStatusInput = {
   hasSyncKey: boolean
   isRemoteRefreshInFlight: boolean
   syncFailure: SyncFailureState
}

/**
 * Builds the sync status shown while cloud sync is failing and retrying.
 *
 * @param syncFailure - The current sync-failure state.
 * @returns The localized status message.
 */
function buildSyncFailureStatusMessage(syncFailure: SyncFailureState) {
   const attemptLabel =
      syncFailure.failureCount > 1 ?
         translate(' (attempt {count})', { count: syncFailure.failureCount })
      :  ''
   const secondsUntilRetry =
      syncFailure.nextRetryAt > Date.now() ?
         Math.max(0, Math.ceil((syncFailure.nextRetryAt - Date.now()) / 1000))
      :  0
   const retryMessage =
      secondsUntilRetry > 0 ?
         translate(" We'll retry automatically in {seconds} {unit}.", {
            seconds: secondsUntilRetry,
            unit: translate(secondsUntilRetry === 1 ? 'second' : 'seconds'),
         })
      :  translate(" We'll retry automatically shortly.")
   const lastAttemptMessage =
      syncFailure.lastFailureTimestamp ?
         ` ${translate('Last attempt {time}', {
            time: new Date(syncFailure.lastFailureTimestamp).toLocaleTimeString(
               getLocale(),
               {
                  hour: '2-digit',
                  minute: '2-digit',
               },
            ),
         })}.`
      :  ''
   return `${translate(
      'Sync key active, but the latest cloud update failed{attemptLabel}.',
      { attemptLabel },
   )}${retryMessage}${lastAttemptMessage}`
}

/**
 * Builds the sync status shown under the active sync key: updating, failing and
 * retrying, or simply active.
 *
 * @param input - The sync-key, refresh, and failure state.
 * @returns The status message, or an empty string without a sync key.
 */
export function buildSyncStatusMessage(input: SyncStatusInput) {
   if (!input.hasSyncKey) {
      return ''
   }
   if (input.isRemoteRefreshInFlight) {
      return translate('Updating...')
   }
   if (input.syncFailure.failureCount > 0) {
      return buildSyncFailureStatusMessage(input.syncFailure)
   }
   return translate('Sync key active.')
}
